import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import PropTypes from 'prop-types';
import YellowCardSvg from '../../assets/event_icons/Yellow_card.png';
import RedCardSvg from '../../assets/event_icons/red_card.png';
import Save from '../../assets/event_icons/save.png';
import Shot from '../../assets/event_icons/shot.png';
import Corner from '../../assets/event_icons/Corner_kick.png';
import Goal from '../../assets/event_icons/Goal.png';
import Opportunity from '../../assets/event_icons/opportunity.png';
import Penalty from '../../assets/event_icons/penatly.png';
import { IEventsData } from '../../types/IEventTrack';

interface TrackProps {
    events?: IEventsData[];
    withStartEndMarkers?: boolean;
    currentMinute?: number;
    endTime?: number;
    playedTeam: string;
}

const Track: React.FC<TrackProps> = ({ events = [], endTime,playedTeam }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const [eventsData, setEventsData] = useState<IEventsData[]>([]);

    useEffect(() => {
        setEventsData(events);
    }, [events]);

    useEffect(() => {
        const svg: d3.Selection<SVGSVGElement, unknown, any, undefined> = d3.select<SVGSVGElement, unknown>(svgRef.current!)
        const createEventTimeline = () => {
            const container = document.getElementById('timeline-container');
            if (!container) return;
            const timelineWidth: number = container.clientWidth;
            const height: number = 100;
            const totalTimeInSeconds = endTime!;
            const xScale: d3.ScaleLinear<number, number> = d3.scaleLinear()
                .domain([0, totalTimeInSeconds]) // Domain in seconds
                .range([0, timelineWidth]);
            svg.selectAll('*').remove();
            svg.append('g')
            svg.append('line')
                .attr('x1', -1)
                .attr('y1', 40)
                .attr('x2', -1)
                .attr('y2', 60)
                .style('stroke-width', 5)
                .style('stroke', '#FFFFFF80');
            svg.append('line')
                .attr('x1', xScale(totalTimeInSeconds) / 2)
                .attr('y1', 40)
                .attr('x2', xScale(totalTimeInSeconds) / 2)
                .attr('y2', 60)
                .style('stroke-width', 2)
                .style('stroke', '#FFFFFF80');
            svg.append('line')
                .attr('x1', xScale(0))
                .attr('y1', height / 2)
                .attr('x2', xScale(Math.min(90 * 60, totalTimeInSeconds)))
                .attr('y2', height / 2)
                .style('stroke', '#FFFFFF80')
                .style('stroke-width', 2);
            // Render dashed line after 90 minutes until end time
            svg.append('line')
                .attr('x1', xScale(90 * 60))
                .attr('y1', height / 2)
                .attr('x2', xScale(totalTimeInSeconds))
                .attr('y2', height / 2)
                .style('stroke', 'white')
                .style('stroke-dasharray', '4');
            // Render homeAway
            svg.selectAll('circle')
                .data(eventsData)
                .enter()
                .append('circle')
                .attr('cx', d => xScale(d.minute * 60 + d.seconds)) // Convert minutes to seconds
                .attr('cy', height / 2)
                .attr('r', 4)
                .style('fill', playedTeam === 'home' ? '#00A1E4' : '#c25757');
            // Render event icons
            svg.selectAll('.data-icon')
                .data(eventsData)
                .enter()
                .append('image')
                .attr('class', 'data-icon')
                .attr('x', d => xScale(d.minute * 60 + d.seconds) - 10)
                .attr('y', height / 2 - 30)
                .attr('xlink:href', d => {
                    switch (d.eventType) {
                        case 'goal':
                            return Goal;
                        case 'yellow_card':
                            return YellowCardSvg;
                        case 'save':
                            return Save;
                        case 'shot_block':
                            return Save;
                        case 'free_kick':
                            return Save;
                        case 'corner':
                            return Corner;
                        case 'shot':
                            return Shot;
                        case 'opportunity':
                            return Opportunity;
                        case 'red_card':
                            return RedCardSvg;
                        case 'penalty':
                            return Penalty;
                        default:
                            return null;
                    }
                });
            // Render solid line at the end
            svg.append('line')
                .attr('x1', timelineWidth)
                .attr('y1', 40)
                .attr('x2', timelineWidth)
                .attr('y2', 60)
                .style('stroke-width', 2)
                .style('stroke', '#FFFFFF80');
        };
        createEventTimeline();
        window.addEventListener('resize', createEventTimeline);
        return () => {
            window.removeEventListener('resize', createEventTimeline);
        };
    }, [eventsData, endTime]);

    return (
        <div id='timeline-container' className='timeline-container' style={{ width: "100%" }}>
            <svg ref={svgRef} width="100.50%" height={100} />
        </div>
    );
};

Track.propTypes = {
    events: PropTypes.array,
    withStartEndMarkers: PropTypes.bool,
    currentMinute: PropTypes.number,
    endTime: PropTypes.number,
};

export default Track;
